import {Component, EventEmitter, Input, Output, ViewEncapsulation, inject} from '@angular/core';
import {HeadlineComponent} from "../../../shared/elements/headline/headline.component";
import {ContentComponent} from "../../../shared/elements/content/content.component";
import {IconBadgeComponent} from "../../../shared/elements/icon-badge/icon-badge.component";
import {DatePipe} from "@angular/common";
import {FormButtonComponent} from "../../../shared/elements/form-button/form-button.component";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {LinkComponent} from "../../../shared/elements/link/link.component";
import {DialogUtility} from "@syncfusion/ej2-popups";
import {Dialog, DialogModule} from "@syncfusion/ej2-angular-popups";
import {LanguageService} from "../../../shared/services/language.service";
import {DialogService} from '../../../shared/services/dialog.service';
import {CalendarStore} from '../stores/calendar-store';
import {CalendarEventResource} from '../../../shared/types/generated/calendarEventResource';
import {createCalendarEventResourceSeriesOverrideEvent} from '../mapper/calendar-api.mapper';

@Component({
    selector: 'app-item-calendar-event',
    imports: [
        HeadlineComponent,
        ContentComponent,
        IconBadgeComponent,
        DatePipe,
        FormButtonComponent,
        TranslatePipe,
        LinkComponent,
        DialogModule
    ],
    templateUrl: './item-calendar-event.component.html',
    styleUrl: './item-calendar-event.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class ItemCalendarEventComponent {
  private languageService: LanguageService = inject(LanguageService);
  public dialogService: DialogService = inject(DialogService);
  public calendarStore: InstanceType<typeof CalendarStore> = inject(CalendarStore);

  @Input() public calenderEvent!: CalendarEventResource;
  @Output() public edit: EventEmitter<CalendarEventResource> = new EventEmitter<CalendarEventResource>();
  @Output() public details: EventEmitter<CalendarEventResource> = new EventEmitter<CalendarEventResource>();

  public deleteTypeDialogVisible: boolean = false
  public editTypeDialogVisible: boolean = false

  private dialogInstance: Dialog | null = null;

  public get displayStartAt(): Date {
    // Date needs to be modified to today, because recurring events return as start_at the first occurence.
    // This is fine because the request returns only events for today.
    // We don't want to change the actual data, because that will then interfere with the edit dialog.
    const startAt = new Date(this.calenderEvent.timing.start_at);
    const displayDate = new Date();
    displayDate.setHours(startAt.getHours(), startAt.getMinutes(), startAt.getSeconds(), startAt.getMilliseconds());
    return displayDate;
  }

  public isMaskedEvent(): boolean {
    return this.calenderEvent.name === null && this.calenderEvent.name_translated === null;
  }

  //#region edit dialogs
  public showEditTypeOverlay(): void {
    if(this.calenderEvent.recurrence.rrule === '' || this.calenderEvent.recurrence.rrule === null) {
      this.edit.emit(this.calenderEvent);
    }
    else {
      this.editTypeDialogVisible = true;
    }
  }

  public editEvent(onlyThisOccurrence: boolean): void {
    this.editTypeDialogVisible = false;
    if(onlyThisOccurrence) {
      const occurrenceDate = new Date();
      const startAt = new Date(this.calenderEvent.timing.start_at);
      occurrenceDate.setHours(startAt.getHours());
      occurrenceDate.setMinutes(startAt.getMinutes());
      occurrenceDate.setSeconds(startAt.getSeconds());
      this.edit.emit(createCalendarEventResourceSeriesOverrideEvent(this.calenderEvent, occurrenceDate, this.calenderEvent.timing.timezone));
    } else {
      this.edit.emit(this.calenderEvent);
    }
  }
  //#endregion edit dialogs
  //#region delete dialogs
  public showDeleteTypeOverlay(): void {
    if(this.calenderEvent.recurrence.rrule === '' || this.calenderEvent.recurrence.rrule === null) {
      this.showDeleteOverlay(false);
    }
    else {
      this.deleteTypeDialogVisible = true;
    }
  }

  public showDeleteOverlay(deleteOccurence: boolean): void {
    this.deleteTypeDialogVisible = false;
    this.dialogInstance = DialogUtility.confirm({
      title: '<h6 class="text-danger fw-bold m-0 pt-1">' + this.languageService.getTranslationByKey('CONFIRMITEMDELETE') + '</h6>',
      content: this.languageService.getTranslationByKey('AREYOUSUREYOUWANTTODELETE'),
      cancelButton: {
        text: this.languageService.getTranslationByKey('CANCEL'),
        click: () => this.showDeleteOverlayCancel()
      },
      okButton: {
        text: this.languageService.getTranslationByKey('DELETE'),
        click: () => this.showDeleteOverlayConfirmed(deleteOccurence),
        cssClass: 'bg-danger brd-danger'
      },
      showCloseIcon: true,
      closeOnEscape: true,
      position: {X: 'center', Y: 'center'},
      animationSettings: {effect: 'Fade'},
      zIndex: 9999
    });
  }

  public getPlainTextPreview(value: string | null): string {
    if(value === null) {
      return '';
    } else {
      const richTextContainer = document.createElement('div');
      richTextContainer.innerHTML = value;
      return richTextContainer.textContent?.trim() ?? '';
    }
  }

  private showDeleteOverlayConfirmed(deleteOccurence: boolean): void {
    if(deleteOccurence) {
      const startAt = new Date(this.calenderEvent.timing.start_at);
      const occurrenceDate = new Date();
      occurrenceDate.setUTCHours(startAt.getUTCHours());
      occurrenceDate.setUTCMinutes(startAt.getUTCMinutes());
      occurrenceDate.setUTCSeconds(startAt.getUTCSeconds());
      this.calendarStore.deleteOccurenceFromSeries(this.calenderEvent.id, this.calenderEvent.calendar_id! ,occurrenceDate);
    } else {
      this.calendarStore.deleteEvent(this.calenderEvent.id, this.calenderEvent.calendar_id!);
    }
    this.dialogInstance?.hide();
  }

  private showDeleteOverlayCancel(): void {
    this.dialogInstance?.hide();
  }
  //#endregion delete dialogs
}
